import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../BaseElement";
import { LightTheme } from "../ThemeLight";

import { styles } from "./PlaceListPicker.style";
import {
    IPlaceList,
    IPlaceListTable,
    makeEmptyPlaceList,
    makeEmptyPlaceListEntry,
} from "../../backend/IPlaceListTable";
import { watch } from "../internal/watch";

import "../PlaceListEditor";
import { ChangeSet, ChangeSetListener } from "../../DataStore/ChangeSet";
import { DataProvider } from "../../DataStore/DataProvider";
import { getModuleLog } from "../../Logger";
import { showFormEntryDialog } from "../FormEntryDialog";
import { PlaceListFields } from "../PlaceListEditor";
import { Kernel } from "../../Kernel";
import { compiledFilterPlaceAsync, compileFilters } from "../../Filter";
import { eachLimit } from "async";
import { IPlace } from "../../../main";

export function showPlaceListEntryDialog(approved?: any) {
    showFormEntryDialog(
        {
            open: true,
            fields: [
                ...PlaceListFields,
                {
                    type: "booleanDropdown",
                    property: "addCurrentPlaces",
                    title: "Add current filtered places",
                },
            ],
            object: Object.assign(makeEmptyPlaceList(), {
                addCurrentPlaces: false,
            }),
            label: "Add new list",
        },
        async (object: any) => {
            try {
                const newList = object.detail as IPlaceList;
                const placeListTable =
                    Kernel.instance().dataStore.getSource<IPlaceListTable>("place-list");

                const list = await placeListTable.create(newList);

                if (list && (newList as any).addCurrentPlaces) {
                    const currentPlaces = Array.from(
                        await Kernel.instance().dataStore.getSource("places").all()
                    );
                    const compiledFilter = compileFilters(
                        Kernel.instance().currentPlaceFilter.get().filter
                    );
                    const filteredPlaces: IPlace[] = [];
                    await eachLimit(currentPlaces, 10, async (x: IPlace) => {
                        if (!(await compiledFilterPlaceAsync(compiledFilter, x))) {
                            filteredPlaces.push(x);
                        }
                    });
                    await eachLimit(
                        filteredPlaces,
                        10,
                        async (x: IPlace) =>
                            await placeListTable.createEntry(
                                list.id,
                                makeEmptyPlaceListEntry({ place: x.id })
                            )
                    );
                }
                if (approved) {
                    approved();
                }
            } catch (e: any) {
                getModuleLog("showPlaceListEntryDialog").error(e);
            }
        },
        () => {}
    );
}

@customElement("ds-place-list-picker")
export class PlaceListPicker extends BaseElement implements ChangeSetListener {
    static override styles = [styles, LightTheme];

    @property({ type: String }) userId?: string;
    @property({ type: String }) layout: "picker" | "dropdown" = "picker";

    @state() protected _lists?: IPlaceList[];
    @state() protected _newList?: IPlaceList;
    @state() protected _selectedList?: IPlaceList;

    private _placeListTable = this._kernel.dataStore.getSource("place-list");
    private _log = getModuleLog("PlaceListPicker");

    @watch("userId")
    async _onUserIdChanged() {
        this._lists = undefined;
        await this._updateLists();
    }

    override async connectedCallback(): Promise<void> {
        super.connectedCallback();

        if (!this.userId) {
            this.userId = this._kernel.user?.id;
        }
        this._placeListTable.addListener(this, []);
    }

    async onChangeSet(provider: DataProvider, _changeSet: ChangeSet<any>): Promise<void> {
        if (provider.name === "place-list") {
            await this._updateLists();
        }
    }

    private async _updateLists() {
        this._lists = undefined;
        if (this.userId) {
            this._lists = Array.from(await this._placeListTable.allOwnedBy(this.userId));
        }
    }

    private _addNewList() {
        showPlaceListEntryDialog();
    }

    private _renderList() {
        if (this.layout === "dropdown") {
            return html`<sl-dropdown>
                <sl-button pill size="medium" slot="trigger" caret>Choose list</sl-button>
                <sl-menu>
                    ${(this._lists ?? []).map(
                        (x) =>
                            html`<sl-menu-item
                                value=${x.id}
                                @click=${(e: any) => {
                                    this._selectedList = this._lists?.find(
                                        (x) => x.id === e.target.value
                                    );
                                    this._raiseEvent("ds-selected", { list: this._selectedList });
                                }}
                                >${x.name}</sl-menu-item
                            >`
                    )}</sl-menu
                ></sl-dropdown
            >`;
        }
        return html`
            <sl-button pill variant="default" @click=${() => this._addNewList()}
                >+ New list</sl-button
            >

            ${this._lists?.map(
                (x) =>
                    html`<div
                        class="flex-column entry ${this._selectedList?.id === x.id ? "active" : ""}"
                        @click=${() => {
                            this._selectedList = x;
                            this._raiseEvent("ds-selected", { list: x });
                        }}
                    >
                        <span>${x.name}</span>
                    </div>`
            )}
        `;
    }

    override render() {
        return html`<div class="container flex-column">${this._renderList()}</div> `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-place-list-picker": PlaceListPicker;
    }
}
